<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useApiClient } from "@/api/client";
import ColorPaletteDO = App.Data.ColorPaletteDO;

const props = defineProps<{
	initialPaletteUuid?: string;
	initialHexColor?: string;
}>();

const isVisible = defineModel<boolean>();
const emit = defineEmits<{
	"user-selects-palette": [uuid: string, startHexColor?: string];
}>();

const palettes = ref<ColorPaletteDO[]>([]);
const selectedPaletteUuid = ref<string | null>(null);
const ready = ref(false);

onMounted(async () => {
	const response = await useApiClient("profile").getColorPalettes();
	if (response.isOk()) {
		palettes.value = response.value;

		// If a palette UUID was pre-selected and exists, skip the dialog
		const matchedPalette = props.initialPaletteUuid
			? response.value.find((p) => p.uuid === props.initialPaletteUuid)
			: null;

		if (matchedPalette) {
			emit("user-selects-palette", matchedPalette.uuid, props.initialHexColor);
			isVisible.value = false;
			return;
		}

		// Fallback: existing logic
		selectedPaletteUuid.value =
			response.value.find((p) => p.is_default)?.uuid ??
			response.value[0]?.uuid ??
			null;

		if (response.value.length === 1 && selectedPaletteUuid.value) {
			emit("user-selects-palette", selectedPaletteUuid.value);
			isVisible.value = false;
			return;
		}

		ready.value = true;
	}
});

const confirm = () => {
	if (!selectedPaletteUuid.value) return;
	emit("user-selects-palette", selectedPaletteUuid.value);
	isVisible.value = false;
};
</script>

<template>
	<v-dialog v-if="ready" v-model="isVisible" persistent width="600">
		<v-card elevation="9">
			<v-toolbar flat>
				<v-toolbar-title>
					<i class="fa-duotone fa-palette fa-lg mr-3" />
					Select Color Palette
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon="mdi-close" @click="isVisible = false" />
			</v-toolbar>

			<v-divider />

			<div class="pa-5">
				<v-select
					v-model="selectedPaletteUuid"
					:items="palettes"
					item-title="name"
					item-value="uuid"
					label="Palette"
					variant="filled"
					hide-details
				/>
			</div>

			<v-divider />

			<v-card-actions class="mx-2 my-1">
				<v-spacer />
				<v-btn color="error" variant="text" @click="isVisible = false"
					>Cancel</v-btn
				>
				<v-btn
					color="secondary"
					variant="flat"
					:disabled="!selectedPaletteUuid"
					@click="confirm"
				>
					Apply
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	<v-dialog v-else>
		<div>{{ selectedPaletteUuid }}</div>
	</v-dialog>
</template>
